"use client"

import copy from "copy-to-clipboard"
import { Share2, Facebook, Mail, Send, Copy, CheckCheck, MessageCircle } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { useBearStore } from "@/lib/stores/user"

interface ShareWidgetProps {
	title: string
	header?: string
}

export default function ShareWidget({
	title,
	header = "Chia sẻ khóa học",
}: ShareWidgetProps) {
	const [isCopied, setIsCopied] = useState(false)
	const baseUrl = typeof window !== "undefined" ? window.location.href : ""
	const { profileUser } = useBearStore()

	// Create share URL with affiliate code if user has one
	const shareUrl = profileUser?.affiliate_code
		? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}discountCode=${profileUser.affiliate_code}`
		: baseUrl

	// Convert decimal to percentage (e.g., 0.05 -> 5)
	const discountPercentage = profileUser?.discount_codes_given
		? Math.round(profileUser.discount_codes_given * 100)
		: 0

	// Create copy text with discount message if user has affiliate code
	const copyText = profileUser?.affiliate_code
		? `Giảm giá ${discountPercentage}% với mã: ${profileUser.affiliate_code}! Khám phá các khóa học chất lượng cao tại GPEMS. Đăng ký ngay để nhận ưu đãi! ${shareUrl}`
		: `${title} ${shareUrl}`

	const handleFacebookShare = () => {
		const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(copyText)}`
		window.open(fbShareUrl, "_blank", "width=600,height=400")
	}

	const handleTelegramShare = () => {
		const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(copyText)}`
		window.open(telegramShareUrl, "_blank", "width=600,height=400")
	}

	const handleEmailShare = () => {
		const emailSubject = encodeURIComponent(title)
		const emailBody = encodeURIComponent(copyText)
		window.location.href = `mailto:?subject=${emailSubject}&body=${emailBody}`
	}

	const handleMessengerShare = () => {
		const messengerShareUrl = `fb-messenger://share?link=${encodeURIComponent(shareUrl)}&redirect_uri=${encodeURIComponent(window.location.origin)}`
		// For Messenger, we'll use a fallback that includes the text
		const fallbackUrl = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&redirect_uri=${encodeURIComponent(window.location.origin)}`
		window.open(fallbackUrl, "_blank", "width=600,height=400")
	}

	const handleCopyToClipboard = async () => {
		try {
			copy(copyText)
			setIsCopied(true)
			toast.success("Link copied to clipboard!")
			setTimeout(() => setIsCopied(false), 2000)
		} catch (error) {
			console.error("Error copying to clipboard:", error)
			toast.error("Failed to copy link")
		}
	}

	return (
		<div className="my-2 rounded-lg border border-gray-200 shadow-sm">
			<div className="space-y-4 rounded-lg bg-white">
				<div className="rounded-tl-lg rounded-tr-lg border-gray-200 border-b bg-gray-50 px-3 py-2 md:px-6 md:py-4">
					<div className="flex items-center space-x-2">
						<Share2 className="h-5 w-5 text-gray-600" />
						<h3 className="font-semibold text-gray-900 text-lg">{header}</h3>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-4 p-4">
					<button
						type="button"
						onClick={handleFacebookShare}
						className="flex items-center gap-2 rounded-md bg-[#1877F2] px-4 py-2 text-white transition-colors hover:bg-[#1864D9]"
					>
						<Facebook className="h-5 w-5" />
						<span>Facebook</span>
					</button>
					<button
						type="button"
						onClick={handleTelegramShare}
						className="flex items-center gap-2 rounded-md bg-[#0088cc] px-4 py-2 text-white transition-colors hover:bg-[#0077b3]"
					>
						<Send className="h-5 w-5" />
						<span>Telegram</span>
					</button>
					<button
						type="button"
						onClick={handleEmailShare}
						className="flex items-center gap-2 rounded-md bg-gray-600 px-4 py-2 text-white transition-colors hover:bg-gray-700"
					>
						<Mail className="h-5 w-5" />
						<span>Email</span>
					</button>
					<button
						type="button"
						onClick={handleMessengerShare}
						className="flex items-center gap-2 rounded-md bg-[#00B2FF] px-4 py-2 text-white transition-colors hover:bg-[#00A1E6]"
					>
						<MessageCircle className="h-5 w-5" />
						<span>Messenger</span>
					</button>
					<button
						type="button"
						onClick={handleCopyToClipboard}
						className="flex items-center gap-2 rounded-md bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200"
					>
						{isCopied ? (
							<CheckCheck className="h-5 w-5 text-green-600" />
						) : (
							<Copy className="h-5 w-5" />
						)}
						<span>{isCopied ? "Copied!" : "Copy Link"}</span>
					</button>
				</div>
			</div>
		</div>
	)
}
